"use client";

import {Box, BoxProps, HStack, Link, LinkProps, StackProps} from "@chakra-ui/react";
import {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import NextLink from "next/link";
import {Container} from "../Container";
import {Logo} from "../Logo";

export const LeftNav = ({children, ...props}: StackProps) => {
  const light = useContext(NavContext);

  return (
    <HStack align="center" gap={[0, 5]} {...props}>
      <Link asChild aria-label="Home" mt="-3px" outline="none">
        <NextLink href="/">
          <Logo h="6" w="auto" showText light={light} />
        </NextLink>
      </Link>
      {children && <HStack gap={2}>{children}</HStack>}
    </HStack>
  );
};

export const NavContext = createContext(false);

export const Nav = ({children, light, ...props}: {light?: boolean} & BoxProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  light = light ?? !stuck;

  const checkStuck = useCallback(() => {
    if (ref.current) {
      // iOS fluctutates between -0.3 and +0.3 on bounding client rect...
      const stuck = ref.current.getBoundingClientRect().y < 1 && window.scrollY > 0;
      setStuck(stuck);
    }
  }, [ref, setStuck]);

  useEffect(() => {
    window.addEventListener("scroll", checkStuck);
    return () => window.removeEventListener("scroll", checkStuck);
  }, [checkStuck]);

  useEffect(checkStuck, [ref.current, checkStuck]);

  return (
    <NavContext.Provider value={light}>
      <Box ref={ref} as="header" position="sticky" top={0} h={0} zIndex="2" {...props}>
        <Box
          w="full"
          bg={light ? "transparent" : "white"}
          borderBottom="1px solid"
          borderBottomColor={light ? "transparent" : "rgba(0,0,0,.1)"}
        >
          <Container py={4} as="nav" display="flex" justifyContent="space-between" alignItems="center">
            {children}
          </Container>
        </Box>
      </Box>
    </NavContext.Provider>
  );
};

export const NavLink = ({color = "white", children, ...props}: LinkProps) => {
  const light = useContext(NavContext);
  return (
    <Link
      asChild
      display="inline-block"
      px="2"
      py="1"
      fontSize="md"
      rounded="lg"
      fontWeight="500"
      textDecoration="none"
      outline="none"
      _hover={{bg: "colorPalette.50", color: "colorPalette.500"}}
      {...props}
      color={light ? color : "gray.700"}
    >
      <NextLink href={props.href ?? ""}>{children}</NextLink>
    </Link>
  );
};
